import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FaSearch, FaBuilding, FaQuestionCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { businessApi } from '../services/api';
import faqData from './faq'; // We'll extract FAQ data for search
import './About.css';

// Helper to get query param
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Helper to highlight search term
function highlight(text, term) {
  if (!text) return '';
  if (!term) return text;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
}

// Static About content for search
const aboutContent = [
  {
    section: 'About Us',
    text: `Connecting Communities with Trusted Businesses. We're on a mission to revolutionize how people discover and connect with local businesses. Our platform bridges the gap between communities and the services they need, creating meaningful connections that drive growth and success.`
  },
  {
    section: 'Our Story',
    text: `Founded in 2020, Business Directory began as a simple idea: to make it easier for people to find and connect with local businesses they can trust. What started as a small startup has grown into a comprehensive platform serving thousands of businesses and users.`
  },
  {
    section: 'Our Mission',
    text: `To empower local businesses and communities by creating meaningful connections that drive growth, trust, and success for everyone involved.`
  },
  {
    section: 'Our Vision',
    text: `To become the world's most trusted platform for discovering and connecting with local businesses, fostering stronger communities worldwide.`
  }
];

const SearchResults = () => {
  const query = useQuery().get('q') || '';
  const [businessResults, setBusinessResults] = useState([]);
  const [faqResults, setFaqResults] = useState([]);
  const [aboutResults, setAboutResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      // 1. Search businesses
      let businesses = [];
      try {
        const allBusinesses = await businessApi.fetchAll();
        businesses = allBusinesses.filter(b =>
          b.name?.toLowerCase().includes(query.toLowerCase()) ||
          b.description?.toLowerCase().includes(query.toLowerCase()) ||
          b.category?.toLowerCase().includes(query.toLowerCase())
        );
      } catch (e) {
        businesses = [];
      }
      setBusinessResults(businesses);

      // 2. Search FAQ (static)
      let faqs = [];
      try {
        // If faqData is a component, extract the data array
        const data = faqData?.default?.faqData || faqData?.faqData || faqData;
        faqs = [];
        if (Array.isArray(data)) {
          data.forEach(cat => {
            cat.questions.forEach(q => {
              if (
                q.question.toLowerCase().includes(query.toLowerCase()) ||
                q.answer.toLowerCase().includes(query.toLowerCase())
              ) {
                faqs.push({
                  category: cat.category,
                  question: q.question,
                  answer: q.answer
                });
              }
            });
          });
        }
      } catch (e) { }
      setFaqResults(faqs);

      // 3. Search About (static)
      const about = aboutContent.filter(
        s =>
          s.section.toLowerCase().includes(query.toLowerCase()) ||
          s.text.toLowerCase().includes(query.toLowerCase())
      );
      setAboutResults(about);
      setLoading(false);
    };
    if (query) fetchResults();
    else {
      setBusinessResults([]);
      setFaqResults([]);
      setAboutResults([]);
      setLoading(false);
    }
  }, [query]);

  return (
    <div className="about-page">
      <section className="hero-section" style={{ minHeight: '200px', padding: '3rem 0 1rem' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h1 className="hero-title">
                <FaSearch /> Search Results
              </h1>
              <p className="hero-subtitle">
                Showing results for <strong>{query}</strong>
              </p>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="content-section">
        <Container>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              {/* Businesses */}
              <Row className="mb-5">
                <Col>
                  <h3><FaBuilding /> Businesses</h3>
                  {businessResults.length === 0 ? (
                    <p>No businesses found.</p>
                  ) : (
                    businessResults.map(b => (
                      <Card key={b.id} className="mb-3">
                        <Card.Body>
                          <Card.Title>
                            <Link to={`/business/${b.id}`}>{highlight(b.name, query)}</Link>
                            {b.category && (
                              <Badge bg="primary" className="ms-2">{b.category}</Badge>
                            )}
                          </Card.Title>
                          <Card.Text>{highlight(b.description, query)}</Card.Text>
                          <Link to={`/business/${b.id}`} className="btn btn-primary btn-sm">
                            View Details <FaArrowRight />
                          </Link>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </Col>
              </Row>
              {/* FAQ */}
              <Row className="mb-5">
                <Col>
                  <h3><FaQuestionCircle /> FAQ</h3>
                  {faqResults.length === 0 ? (
                    <p>No FAQ matches found.</p>
                  ) : (
                    faqResults.map((f, i) => (
                      <Card key={i} className="mb-3">
                        <Card.Body>
                          <Card.Title>{highlight(f.question, query)}</Card.Title>
                          <Card.Text>{highlight(f.answer, query)}</Card.Text>
                          <Badge bg="secondary">{f.category}</Badge>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </Col>
              </Row>
              {/* About */}
              <Row className="mb-5">
                <Col>
                  <h3><FaInfoCircle /> About</h3>
                  {aboutResults.length === 0 ? (
                    <p>No about info found.</p>
                  ) : (
                    aboutResults.map((a, i) => (
                      <Card key={i} className="mb-3">
                        <Card.Body>
                          <Card.Title>{highlight(a.section, query)}</Card.Title>
                          <Card.Text>{highlight(a.text, query)}</Card.Text>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </Col>
              </Row>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default SearchResults; 